/**
 * useBusinessCRM.js — hook extracted from BusinessCRMFields.
 *
 * Encapsulates ALL network operations (fetch schema, add tag, add note,
 * save follow-up, save/clear custom field value, create/delete field schema)
 * so the visual component is just markup + callbacks.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";

export default function useBusinessCRM(business, onUpdate) {
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSchema = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/settings/contact-fields`);
      if (r?.ok) setSchema((await r.json()).custom || []);
    } catch (err) {
      console.warn("[business-crm] loadSchema failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSchema(); }, [loadSchema]);

  const patchBusiness = useCallback(async (payload) => {
    const res = await authFetch(`${API}/businesses/${business.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res?.ok) throw new Error("PUT failed");
    const fresh = await res.json();
    if (onUpdate) onUpdate({ ...business, ...fresh });
    return fresh;
  }, [business, onUpdate]);

  // ── Tags ─────────────────────────────────────────────────────────────
  const tags = Array.isArray(business.tags) ? business.tags : [];
  const addTag = useCallback(async (raw) => {
    const t = (raw || "").trim();
    if (!t || tags.includes(t)) return false;
    try { await patchBusiness({ tags: [...tags, t] }); return true; }
    catch (err) { console.warn("[business-crm] addTag failed:", err?.message); toast.error("Error al agregar etiqueta"); return false; }
  }, [tags, patchBusiness]);
  const removeTag = useCallback(async (t) => {
    try { await patchBusiness({ tags: tags.filter((x) => x !== t) }); }
    catch (err) { console.warn("[business-crm] removeTag failed:", err?.message); toast.error("Error al quitar etiqueta"); }
  }, [tags, patchBusiness]);

  // ── Notes ────────────────────────────────────────────────────────────
  const notes = Array.isArray(business.notes) ? business.notes : [];
  const addNote = useCallback(async (raw) => {
    const t = (raw || "").trim();
    if (!t) return false;
    const next = [...notes, { id: `n_${Date.now()}`, text: t, created_at: new Date().toISOString() }];
    try { await patchBusiness({ notes: next }); return true; }
    catch (err) { console.warn("[business-crm] addNote failed:", err?.message); toast.error("Error al guardar la nota"); return false; }
  }, [notes, patchBusiness]);
  const removeNote = useCallback(async (noteKey) => {
    try { await patchBusiness({ notes: notes.filter((n) => (n.id || n.text) !== noteKey) }); }
    catch (err) { console.warn("[business-crm] removeNote failed:", err?.message); toast.error("Error al borrar la nota"); }
  }, [notes, patchBusiness]);

  // ── Follow-up ────────────────────────────────────────────────────────
  const saveFollowup = useCallback(async (field, value) => {
    try { await patchBusiness({ [field]: value }); }
    catch (err) { console.warn("[business-crm] saveFollowup failed:", err?.message); toast.error("Error al guardar el seguimiento"); }
  }, [patchBusiness]);

  // ── Custom field values ──────────────────────────────────────────────
  const saveCustomField = useCallback(async (fieldId, value) => {
    const nextCustom = { ...(business.custom || {}), [fieldId]: value };
    if (!value && value !== 0) delete nextCustom[fieldId];
    await patchBusiness({ custom: nextCustom });
  }, [business, patchBusiness]);
  const clearCustomField = useCallback(async (fieldId) => {
    try { await saveCustomField(fieldId, ""); toast.success("Valor eliminado"); }
    catch (err) { console.warn("[business-crm] clearCustomField failed:", err?.message); }
  }, [saveCustomField]);

  // ── Schema management ────────────────────────────────────────────────
  const createFieldSchema = useCallback(async (draft) => {
    const label = (draft.label || "").trim();
    const fid = ((draft.field_id || "").trim() || label)
      .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
    if (!label || !fid) { toast.error("El nombre es obligatorio"); return false; }
    try {
      const res = await authFetch(`${API}/settings/contact-fields`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label, field_id: fid, type: draft.type || "text", visibility: "always" }),
      });
      if (!res) return false;
      if (res.status === 409) { toast.error(`Ya existe un campo "${fid}"`); return false; }
      if (!res.ok) { toast.error("Error al crear el campo"); return false; }
      toast.success(`Campo "${label}" creado — usalo como {{campo.${fid}}}`);
      await loadSchema();
      return true;
    } catch (err) { console.warn("[business-crm] createFieldSchema failed:", err?.message); toast.error("Error creando el campo"); return false; }
  }, [loadSchema]);

  const deleteFieldSchema = useCallback(async (s) => {
    if (!window.confirm(
      `¿Eliminar el campo "${s.label}"? Dejará de existir como variable {{campo.${s.field_id}}}.`
    )) return;
    try {
      const res = await authFetch(`${API}/settings/contact-fields/${s.id}`, { method: "DELETE" });
      if (!res?.ok) { toast.error("No se pudo eliminar"); return; }
      toast.success("Campo eliminado");
      await loadSchema();
    } catch (err) { console.warn("[business-crm] deleteFieldSchema failed:", err?.message); toast.error("Error al eliminar"); }
  }, [loadSchema]);

  return {
    schema, loading,
    tags, addTag, removeTag,
    notes, addNote, removeNote,
    saveFollowup,
    saveCustomField, clearCustomField,
    createFieldSchema, deleteFieldSchema,
  };
}
